import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';

const ENTERPRISE_TYPES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Real Estate',
  'Education',
  'Retail',
  'Manufacturing',
  'Consulting',
  'Marketing',
  'Other',
];

export const PageSignUp = memo(() => {
  const [form, setForm] = useState({
    name: '', email: '', company_name: '', phone: '',
    start_date: '', end_date: '', enterprise_type: '',
  });
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [vis, setVis] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'GOtaps - Register Enterprise';
    setTimeout(() => setVis(true), 80);
  }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setError('');
    setFieldErrors(e => ({ ...e, [k]: null }));
  };

  const handleSubmit = async () => {
    setError(''); setFieldErrors({}); setSuccess('');

    // Basic validation
    const required = ['name', 'email', 'company_name', 'phone', 'start_date', 'end_date', 'enterprise_type'];
    const missing = required.filter(k => !form[k].trim());
    if (missing.length) {
      setError('Please fill in all required fields.');
      const errs = {};
      missing.forEach(k => errs[k] = 'Required');
      setFieldErrors(errs);
      return;
    }
    if (!contract) {
      setError('Please upload a contract file.');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('contract', contract);

      await api.registerEnterprise(fd);
      setSuccess('Registration submitted successfully! Your application is under review. You will receive an email once approved.');
    } catch (e) {
      if (e.errors) {
        setFieldErrors(e.errors);
        const firstMsg = Object.values(e.errors).flat()[0];
        setError(firstMsg || e.message);
      } else {
        setError(e.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => { if (e.key === 'Enter') handleSubmit(); };

  const fieldErr = (k) => {
    const e = fieldErrors[k];
    return e ? (Array.isArray(e) ? e[0] : e) : null;
  };

  return (
    <>
      <style>{`
        @keyframes orbDrift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(38px,-28px) scale(1.07)}}
        @keyframes orbDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-32px,22px) scale(1.05)}}
        @keyframes orbDrift3{0%,100%{transform:translate(0,0)}50%{transform:translate(22px,18px)}}
        @keyframes borderTrail{0%{background-position:0%}100%{background-position:200%}}
        .si-card::before{content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(100,116,139,.5),rgba(212,175,55,.45),rgba(100,116,139,.5),transparent);background-size:200%;animation:borderTrail 4s linear infinite;border-radius:30px 30px 0 0}
      `}</style>

      {/* Ambient orbs */}
      <div className="si-scene">
        <div className="si-orb si-orb-1"/>
        <div className="si-orb si-orb-2"/>
        <div className="si-orb si-orb-3"/>
        <div className="si-orb si-orb-4"/>
        <div className="si-noise"/>
      </div>

      {/* Center card */}
      <div style={{
        position:"fixed", inset:0, zIndex:10,
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"20px", pointerEvents:"none",
      }}>
        <div style={{
          pointerEvents:"all",
          opacity:vis?1:0,
          transform:vis?"none":"translateY(20px) scale(.97)",
          transition:"opacity .55s ease 0s, transform .55s cubic-bezier(.16,1,.3,1) 0s",
        }}>
          <div className="si-card" style={{ maxWidth:480, display:'flex', flexDirection:'column', maxHeight:'90vh' }}>
            {/* Fixed header */}
            <div style={{ flexShrink:0 }}>
              <div className="si-logo">
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="GOtaps" style={{height:40,objectFit:'contain'}}/>
                <div className="si-logo-text">
                  <span className="si-logo-tag">Enterprise</span>
                </div>
              </div>
              <div className="si-title">Register Enterprise</div>
              <div className="si-sub">Submit your application to get started</div>

              {error && <div className="si-error"><span>⚠️</span> {error}</div>}
              {success && <div className="si-success"><span>✅</span> {success}</div>}
            </div>

            {/* Scrollable form area */}
            {!success && (
              <div style={{
                overflowY:'auto', flex:1, paddingRight:4,
                scrollbarWidth:'thin', scrollbarColor:'rgba(0,0,0,.1) transparent',
              }}>
                {/* Name & Email row */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:0}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Name *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('name')?2:10}}>
                      <span className="si-inp-ico">👤</span>
                      <input className="si-inp" placeholder="Full name" value={form.name} onChange={e=>set('name',e.target.value)} onKeyDown={handleKey} style={fieldErr('name')?{borderColor:'#dc2626'}:{}}/>
                    </div>
                    {fieldErr('name')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('name')}</div>}
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Email *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('email')?2:10}}>
                      <span className="si-inp-ico">📧</span>
                      <input className="si-inp" type="email" placeholder="Email address" value={form.email} onChange={e=>set('email',e.target.value)} onKeyDown={handleKey} style={fieldErr('email')?{borderColor:'#dc2626'}:{}}/>
                    </div>
                    {fieldErr('email')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('email')}</div>}
                  </div>
                </div>

                {/* Company & Phone row */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:0}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Company Name *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('company_name')?2:10}}>
                      <span className="si-inp-ico">🏢</span>
                      <input className="si-inp" placeholder="Company name" value={form.company_name} onChange={e=>set('company_name',e.target.value)} onKeyDown={handleKey} style={fieldErr('company_name')?{borderColor:'#dc2626'}:{}}/>
                    </div>
                    {fieldErr('company_name')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('company_name')}</div>}
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Phone Number *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('phone')?2:10}}>
                      <span className="si-inp-ico">📱</span>
                      <input className="si-inp" type="tel" placeholder="Phone number" value={form.phone} onChange={e=>set('phone',e.target.value)} onKeyDown={handleKey} style={fieldErr('phone')?{borderColor:'#dc2626'}:{}}/>
                    </div>
                    {fieldErr('phone')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('phone')}</div>}
                  </div>
                </div>

                {/* Start & End Date row */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:0}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Start Date *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('start_date')?2:10}}>
                      <span className="si-inp-ico">📅</span>
                      <input className="si-inp" type="date" value={form.start_date} onChange={e=>set('start_date',e.target.value)} style={{...fieldErr('start_date')?{borderColor:'#dc2626'}:{}}}/>
                    </div>
                    {fieldErr('start_date')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('start_date')}</div>}
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>End Date *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('end_date')?2:10}}>
                      <span className="si-inp-ico">📅</span>
                      <input className="si-inp" type="date" value={form.end_date} onChange={e=>set('end_date',e.target.value)} style={{...fieldErr('end_date')?{borderColor:'#dc2626'}:{}}}/>
                    </div>
                    {fieldErr('end_date')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('end_date')}</div>}
                  </div>
                </div>

                {/* Enterprise Type & Contract row */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:0}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Enterprise Type *</label>
                    <div className="si-inp-wrap" style={{marginBottom:fieldErr('enterprise_type')?2:10}}>
                      <span className="si-inp-ico">🏷️</span>
                      <select
                        className="si-inp"
                        value={form.enterprise_type}
                        onChange={e=>set('enterprise_type',e.target.value)}
                        style={{...fieldErr('enterprise_type')?{borderColor:'#dc2626'}:{}, paddingLeft:38, appearance:'none', cursor:'pointer'}}
                      >
                        <option value="">Select Enterprise Type</option>
                        {ENTERPRISE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {fieldErr('enterprise_type')&&<div style={{fontSize:10,color:'#dc2626',marginBottom:8}}>{fieldErr('enterprise_type')}</div>}
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:620,color:'#475569',marginBottom:4,display:'block'}}>Upload Contract *</label>
                    <div className="si-inp-wrap" style={{marginBottom:10}}>
                      <span className="si-inp-ico">📎</span>
                      <input
                        className="si-inp"
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={e=>{ setContract(e.target.files[0]||null); setError(''); }}
                        style={{paddingLeft:38, cursor:'pointer', fontSize:11}}
                      />
                    </div>
                    {contract && <div style={{fontSize:10,color:'#059669',marginBottom:8}}>{contract.name}</div>}
                  </div>
                </div>
              </div>
            )}

            {/* Fixed footer */}
            <div style={{ flexShrink:0, marginTop: success ? 0 : 6 }}>
              {!success && (
                <button className="si-btn" disabled={loading} onClick={handleSubmit} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9}}>
                  {loading?<><div className="si-spinner"/>Submitting...</>:<><span>Register Enterprise</span><span style={{opacity:.7,fontSize:16}}>→</span></>}
                </button>
              )}

              <div className="si-footer" style={{marginTop:14}}>
                Already have an account? <button className="si-footer-link" onClick={()=>navigate('/login')}>Sign in</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
